'use client';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import SpriteIcon from '../../shared/sprite-icon';
import { useTranslation } from '../../../hooks/use-translation';
import { OwAppTile } from '../../../interfaces/domain/ow-app-tile.modal';
import { useClassName } from '../../../hooks/use-class-name';
import useIsMobile from '../../../hooks/use-is-mobile';
import { MainDownloadBtn } from '../../download-section/main-dl-btn';
import { MultiDownloadList } from '../../download-section/multi-dl-list';
import OWLink from '../../shared/ow-link';
import {
  useDownloadClickHandler,
  DownloadClickComponent,
} from '../../../hooks/use-download-click-handler';
import { useInstalledExtensions } from '../../../hooks/use-installed-extensions';
import { useDownloadInProgress } from '../../../hooks/use-download-in-progress';
import { useOverwolfStore } from '../../../hooks/use-overwolf-store';
import { useDownloadLabel } from '../../../hooks/use-download-label';

// -----------------------------------------------------------------------------
interface AppsShelveItemProps {
  item: OwAppTile;
  isOpen: boolean;
  onClick: () => void;
  // Whether to show the "Installed" overlay tag on the thumbnail. Defaults to
  // true; the Library (My Account) passes false since every app there is, by
  // definition, already installed.
  showInstalledTag?: boolean;
  // When set, renders a persistent footer (name / price) beneath the thumbnail —
  // used by the Library grid. The hover info-card still overlays it on hover.
  showLibraryFooter?: boolean;
  // Name of the shelf this tile belongs to, forwarded as origin.shelf on the
  // native install call. Undefined for grids with no shelf concept (search
  // results, Library).
  shelfName?: string;
}

// -----------------------------------------------------------------------------
const AppsShelveItem: FC<AppsShelveItemProps> = ({
  item,
  isOpen,
  onClick,
  showInstalledTag = true,
  showLibraryFooter = false,
  shelfName,
}) => {
  const { t } = useTranslation();
  const showInfoBtnRef = useRef<HTMLButtonElement>(null);
  const mobileDLBtnRef = useRef<HTMLAnchorElement>(null);
  const multiMenuContainer = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [multIMenuIsOpen, setIsMultIMenuIsOpen] = useState<boolean>(false);
  const [topStyle, setTopStyle] = useState<number>(0);

  // ---------------------------------------------------------------------------
  const { handleClickDownload } = useDownloadClickHandler(
    {
      item,
      component: DownloadClickComponent.ShelveTile,
      shelf: shelfName,
    },
  );

  const { isAvailable: isOverwolfStoreAvailable } = useOverwolfStore();
  const { installedAppIds, installedElectronAppIds } = useInstalledExtensions();
  const isInstalled = !item.isElectron && installedAppIds.has(item.id);
  // Electron apps aren't native store apps; their install state comes from the
  // registry-backed set. When installed, the button launches instead of installs.
  const isElectronInstalled =
    item.isElectron && installedElectronAppIds.has(item.id);
  // Per-download label helper (only the installed download's button renames).
  const getDownloadLabel = useDownloadLabel();

  // Inside the Overwolf client (iframe + installed) only Windows downloads are
  // runnable, so non-Windows download buttons are hidden there.
  const availableDownloads = (() => {
    const all = item.alternativeDownloads ?? [];
    return isOverwolfStoreAvailable
      ? all.filter((d) => d.platform === 'Windows')
      : all;
  })();

  const multiDownloadsList = (() => {
    const all = availableDownloads;
    if (!isInstalled || all.length < 2) {
      return all;
    }

    const matchIdx = all.findIndex((d) => d.appId === item.id);
    if (matchIdx <= 0) {
      return all;
    }

    const match = all[matchIdx];
    const originalMain = all[0];
    const rest = all.filter((_, i) => i !== 0 && i !== matchIdx);
    return [match, originalMain, ...rest];
  })();

  const mainDownload = multiDownloadsList[0];

  // In-progress (download or native install) state for the main button; an
  // alternative download lights up its own row in the menu (see MultiDownloadList).
  const downloadInProgress = useDownloadInProgress();
  const isDownloading = downloadInProgress(mainDownload?.appId ?? item.id);

  // Per-download label: only the button whose own app is installed switches its
  // verb to launch (Electron or native), e.g. "Download standalone" ->
  // "Launch standalone". Label-less cases fall back to the generic download label.
  const mainBtnLabel =
    getDownloadLabel(mainDownload, item.id, Boolean(item.isElectron)) ||
    t('downloadApp');
  // Electron downloads go through the shell's downloadApp (an installer file
  // download) rather than installApp — the in-progress button verb differs.
  const mainIsElectron = mainDownload?.isElectron ?? Boolean(item.isElectron);
  // The mobile button is a plain link to the app page — there's no client to
  // launch into on mobile — so it always keeps the original "Download" verb and
  // never the installed->launch rename getDownloadLabel applies for desktop.
  const mobileBtnLabel = mainDownload?.label || t('downloadApp');
  const mainDownloadUrl = (() => {
    if (!mainDownload?.url) {
      return '';
    }
    try {
      return new URL(mainDownload.url).toString();
    } catch {
      // Some store-provided alternativeDownloads carry a non-absolute / malformed
      // url (constructDownloadUrl leaves an already-set url untouched); new URL()
      // would throw on those and crash the whole tile/grid, so fall back to the
      // raw string.
      return mainDownload.url;
    }
  })();
  // ---------------------------------------------------------------------------
  const sectionClassName = useClassName(
    {
      'is-shown': isOpen,
    },
    'app-item',
  );

  // ---------------------------------------------------------------------------
  const multiClassName = useClassName(
    {
      'is-open': multIMenuIsOpen,
    },
    'multi-downloads-menu multi-tile-menu',
  );

  // ---------------------------------------------------------------------------
  const toggleMultiMenu = (): void => {
    if (multiMenuContainer.current) {
      const rect = multiMenuContainer.current.getBoundingClientRect();
      setTopStyle(rect.top + 40);
    }
    setIsMultIMenuIsOpen((open) => !open);
  };

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (availableDownloads.length === 1) return;

    const handleClickOutside = (e: globalThis.MouseEvent): void => {
      const elm = e.target as HTMLElement;
      if (
        elm.closest('.option-item-url') ||
        elm.closest('.option-item-btn') ||
        elm.closest('.multi-options-btn')
      ) {
        return;
      }
      setIsMultIMenuIsOpen(false);
    };

    const handleScroll = (): void => {
      setIsMultIMenuIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  // ---------------------------------------------------------------------------

  const rateStyle = useMemo<React.CSSProperties>(() => {
    if (!item.avgReviewRate) {
      return;
    }
    // round to nearest .5 score, to support 5 stars visual in mobile.
    return {
      '--rating': Math.round(item.avgReviewRate * 2) / 2,
    } as React.CSSProperties;
  }, [item]);

  // ---------------------------------------------------------------------------
  if (!item || !item.title) {
    return null;
  }

  const href = isMobile
    ? `/app/${item.seoName}?getapp`
    : `/app/${item.seoName}`;

  return (
    <li className={`${sectionClassName}${showLibraryFooter ? ' library-tile' : ''}`}>
      <button
        className='icon-btn show-info-btn'
        title='show more info'
        onClick={onClick}
        ref={showInfoBtnRef}
      >
        <SpriteIcon svgClassName='icon-info' icon='alert' />
        <SpriteIcon svgClassName='icon-expand' icon='arrow-down' />
      </button>

      <OWLink href={href} title={item.title}>
        <div className='app-thumb-wrapper'>
          <img src={item.tileUrl} alt={item.title} className='app-thumb' />
          {showInstalledTag && (isInstalled || isElectronInstalled) && (
            <div className='installed-indicator'>
              <SpriteIcon icon='check' svgClassName='installed-icon' />
              {t('installed')}
            </div>
          )}
        </div>

        <div className='info-card'>
          <div className='card-header'>
            <img src={item.iconUrl} alt={item.title} className='app-icon' />

            <div className='title'>
              <h3>{item.title}</h3>

              {item.isFree && <span className='type'>{t('freeOption')}</span>}

              <div className='star-rating star-color-2' style={rateStyle} />
            </div>
          </div>
          <div className='card-body'>
            <p>{item.shortDescription}</p>
          </div>
        </div>
      </OWLink>

      {showLibraryFooter && (
        <div className='library-tile-footer'>
          <h3 className='library-tile-footer__name' title={item.title}>
            {item.title}
          </h3>
          {item.isFree && (
            <span className='library-tile-footer__price'>{t('freeOption')}</span>
          )}
        </div>
      )}

      {availableDownloads.length === 1 && (
        <MainDownloadBtn
          appTitle={item.title}
          btnLabel={mainBtnLabel}
          url={new URL(availableDownloads[0].url).toString()}
          className='btn-primary small app-dl'
          isLoading={isDownloading}
          isElectron={mainIsElectron}
          handleClickDownload={handleClickDownload}
        />
      )}

      {availableDownloads.length > 1 && (
        <div className={multiClassName} ref={multiMenuContainer}>
          <div className='main-multi-btn'>
            <MainDownloadBtn
              appTitle={item.title}
              btnLabel={mainBtnLabel}
              url={mainDownloadUrl}
              className='multi-dl-btn'
              isLoading={isDownloading}
              isElectron={mainIsElectron}
              handleClickDownload={handleClickDownload}
            />

            <button
              className='multi-options-btn'
              onClick={toggleMultiMenu}
              title={t('downloadOptions')}
            >
              <SpriteIcon icon='arrow-down' svgClassName='arrow-icon' />
            </button>
          </div>

          <div className='tile-multi-dl-list' style={{ top: `${topStyle}px` }}>
            <MultiDownloadList
              appTitle={item.title}
              dlData={multiDownloadsList}
              skipMain={true}
              mainAppId={item.id}
              itemIsElectron={Boolean(item.isElectron)}
              handleClickDownload={handleClickDownload}
            />
          </div>
        </div>
      )}

      <OWLink
        // eslint-disable-next-line no-warning-comments
        href={href}
        className='btn-primary small link-btn'
        ref={mobileDLBtnRef}
      >
        {mobileBtnLabel}
      </OWLink>
    </li>
  );
};

export default AppsShelveItem;
